import { createRouter, createWebHistory } from "vue-router"
import axios from 'axios';
import { useRouterStore } from '@/stores/router';
import { useAuthStore } from "@/stores/auth";
import { authEndpoint } from "@/api/endpoints/auth";
import { useRuntimeConfig } from "@/composables/useRuntimeConfig";
import { useModoManutencao } from "@/composables/useModoManutencao";
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollToPlugin);

import authRoutes from './modules/auth'
import geralRoutes from "./modules/geral";

import basicosRoutes from '@/modules/basicos/routes/routes.js';
import contratacaoRoutes from '@/modules/contratacao/routes/routes.js';
import crciRoutes from '@/modules/crci/routes/routes.js';
import financeiroRoutes from '@/modules/financeiro/routes/routes.js';
import inscricoesRoutes from '@/modules/inscricoes/routes/routes.js';
import ordenadorRoutes from "@/modules/ordenador/routes/routes";
import pessoasRoutes from '@/modules/pessoas/routes/routes.js';
import rhRoutes from "@/modules/rh/routes/routes";
import projetoRoutes from "@/modules/projeto/routes/routes.js";
import suporteRoutes from "@/modules/suporte/routes/routes.js";
import administrativoRoutes from "@/modules/administrativo/routes/routes.js";
import prestacaoContasRoutes from "@/modules/prestacaoContas/routes/routes.js";
import formulariosRoutes from "@/modules/formularios/routes/routes.js";
import tutoriaisRoutes from "@/modules/tutoriais/routes/routes.js";
import painelGerencialRoutes from "@/modules/painelGerencial/routes/routes.js";

const { moduloFormulario } = useRuntimeConfig();

const routes = [
  ...authRoutes,

  {
    path: '/',
    name: 'main',
    component: () => import('@/views/MainView.vue'),
    meta: { requiresAuth: true },
    children: [
      ...geralRoutes,

      ...basicosRoutes,
      ...contratacaoRoutes,
      ...crciRoutes,
      ...financeiroRoutes,
      ...inscricoesRoutes,
      ...ordenadorRoutes,
      ...pessoasRoutes,
      ...rhRoutes,
      ...projetoRoutes,
      ...administrativoRoutes,
      ...prestacaoContasRoutes,
      ...(moduloFormulario.value ? formulariosRoutes : []),
      ...tutoriaisRoutes,
      ...painelGerencialRoutes,
    ]
  },
  ...suporteRoutes,
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// `meta.permissoes` lista permissões do token (`ação:recurso`); basta ter uma delas.
// Cada nível da rota (pai e filhas) com a lista preenchida precisa ser atendido.
function temPermissoesDaRota(to, authStore) {
  const permissoesUsuario = authStore.getUsuarioPermissoes();
  if (permissoesUsuario.includes('all:all')) return true;

  return to.matched.every((registro) => {
    const exigidas = registro.meta?.permissoes;
    return !exigidas?.length || exigidas.some((permissao) => permissoesUsuario.includes(permissao));
  });
}

let refreshPromise = null;

async function tentarRefreshToken(authStore) {
  if (refreshPromise) return refreshPromise;

  const refreshToken = authStore.getRefreshToken();
  if (!refreshToken) return false;

  const { apiBaseUrl } = useRuntimeConfig();

  refreshPromise = axios.post(
    authEndpoint.refreshToken,
    { refresh_token: refreshToken },
    { baseURL: apiBaseUrl.value, headers: { 'Accept': 'application/json' } }
  ).then((response) => {
    if (response.status === 200) {
      authStore.setToken(response.data.access_token);
      authStore.setRefreshToken(response.data.refresh_token);
      return true;
    }
    return false;
  }).catch(() => {
    return false;
  }).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const { modoManutencao, exibirAvisoManutencao } = useModoManutencao();

  if (modoManutencao.value) {
    if (authStore.isAuthenticated) authStore.logout();
    exibirAvisoManutencao();
    return to.path === '/login' ? next() : next('/login');
  }

  let isAuthenticated = authStore.isAuthenticated;
  if (isAuthenticated) {
    let tokenValido = false;
    try {
      const decoded = authStore.getDecodedToken();
      const exp = decoded?.exp;
      tokenValido = !!decoded && typeof exp === 'number' && exp * 1000 >= Date.now();
    } catch {
      tokenValido = false;
    }

    if (!tokenValido) {
      const refreshed = await tentarRefreshToken(authStore);
      if (!refreshed) {
        authStore.logout();
        isAuthenticated = false;
      }
    }
  }

  const rotaPublica = !to.meta?.requiresAuth;
  const rotaDesconhecida = to.matched.length === 0;
  const rotaRaiz = to.path === '/';

  if (rotaRaiz || rotaDesconhecida) {
    return next(isAuthenticated ? '/inicio' : '/login');
  }

  if (!rotaPublica && !isAuthenticated) {
    return next('/login');
  }

  if (rotaPublica && isAuthenticated && to.path === '/login') {
    return next('/inicio');
  }

  if (!rotaPublica && !temPermissoesDaRota(to, authStore)) {
    return next(from.name ? false : '/inicio');
  }

  next();
});

router.afterEach((to, from) => {
  const routerStore = useRouterStore();
  const authStore = useAuthStore();

  routerStore.definirRotaAtual({ path: to.path, name: to.name, params: to.params });

  if (authStore.isAuthenticated && from.meta?.isRotaOrigem) {
    routerStore.definirRotaOrigem({ path: from.path, name: from.name });
  }

  if (from.path !== '/') {
    routerStore.definirUltimaRotaVisitada({ path: from.path, name: from.name, params: from.params, query: from.query });
  }

  if (window.scrollY > 0) {
    gsap.to(window, { duration: 0.8, scrollTo: 0, ease: 'expo.inOut' });
  }
});

export default router